/**
 * Shared provider refresh availability decision logic.
 * Mirrors backend utils/provider_refresh_metadata.py for consistent UI across entry points.
 */

const REFRESH_STATUS = {
  REFRESH_AVAILABLE_NOW: 'refresh_available_now',
  REFRESH_NOT_AVAILABLE_YET: 'refresh_not_available_yet',
  VIEW_LATEST_ONLY: 'view_latest_only',
  UNKNOWN: 'unknown',
};

export const REFRESH_STATUS_UNAVAILABLE_MESSAGE = 'Refresh status unavailable';

export function buildUnknownRefreshStatusPayload({
  providerKey = '',
  hasCredentials = false,
} = {}) {
  return {
    success: true,
    status: 'unknown',
    can_refresh: true,
    can_refresh_now: true,
    message: REFRESH_STATUS_UNAVAILABLE_MESSAGE,
    provider_key: providerKey,
    has_refresh_status: false,
    refresh_status: REFRESH_STATUS.UNKNOWN,
    provider_refresh_status: REFRESH_STATUS.UNKNOWN,
    refresh_available_date: null,
    banner: null,
  };
}

export function formatRefreshDateMmDdYy(isoDate) {
  if (!isoDate) return null;
  try {
    const dateObj = new Date(isoDate.length === 10 ? `${isoDate}T00:00:00` : isoDate);
    if (Number.isNaN(dateObj.getTime())) return isoDate;
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const yy = String(dateObj.getFullYear()).slice(-2);
    return `${mm}/${dd}/${yy}`;
  } catch {
    return isoDate;
  }
}

export function formatLastUpdatedMmDdYy(isoDate) {
  return formatRefreshDateMmDdYy(isoDate);
}

/**
 * Resolve provider refresh date for display (MM/DD/YY).
 * Priority: raw provider text → ISO date fields → report fallback.
 */
export function resolveProviderRefreshDate(apiData, reportFallbackDate = null) {
  const rawText = apiData?.refresh_available_raw_text
    || apiData?.next_refresh_available_text_raw
    || apiData?.banner?.next_refresh_date_raw;

  if (rawText) {
    const match = String(rawText).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (match) {
      const [, mm, dd, yy] = match;
      const year = yy.length === 2 ? `20${yy}` : yy;
      return formatRefreshDateMmDdYy(`${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`);
    }
  }

  const iso = apiData?.refresh_available_date || apiData?.next_refresh_available_date;
  if (iso) return formatRefreshDateMmDdYy(iso);

  if (reportFallbackDate) return formatRefreshDateMmDdYy(reportFallbackDate);
  return null;
}

function resolveProviderRefreshDateIso(apiData) {
  const rawText = apiData?.refresh_available_raw_text
    || apiData?.next_refresh_available_text_raw;

  if (rawText) {
    const match = String(rawText).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (match) {
      const [, mm, dd, yy] = match;
      const year = yy.length === 2 ? `20${yy}` : yy;
      return `${year}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
  }

  return apiData?.refresh_available_date || apiData?.next_refresh_available_date || null;
}

/**
 * Build action state from API refresh-status payload or nested action object.
 */
export function buildRefreshActionState(apiData, { hasCredentials = false, hasSavedReport = false } = {}) {
  const action = apiData?.action;
  const savedReportExists = hasSavedReport || hasCredentials;
  if (action?.action) {
    const canRefreshNow = action.can_refresh_now ?? apiData?.can_refresh_now ?? false;
    if (!canRefreshNow && savedReportExists && (action.action === 'purchase' || action.action === 'check')) {
      const { action: _nestedAction, ...rest } = apiData;
      return buildRefreshActionState(rest, { hasCredentials, hasSavedReport: savedReportExists });
    }
    let resolvedAction = action.action;
    if (hasCredentials && (resolvedAction === 'view_report' || resolvedAction === 'check')) {
      resolvedAction = 'refresh_now';
    }
    const resolved = {
      ...action,
      action: resolvedAction,
      can_refresh_now: canRefreshNow,
    };
    if (hasCredentials && resolvedAction === 'refresh_now') {
      resolved.secondary_action = null;
      resolved.secondary_label = null;
    }
    return resolved;
  }

  const refreshStatus = apiData?.refresh_status || apiData?.provider_refresh_status || REFRESH_STATUS.UNKNOWN;
  const canRefreshNow = Boolean(
    apiData?.can_refresh_now
    || apiData?.can_refresh
    || refreshStatus === REFRESH_STATUS.REFRESH_AVAILABLE_NOW
    || apiData?.refresh_button_detected
  );
  const nextDate = resolveProviderRefreshDateIso(apiData);
  const nextDateMmDdYy = resolveProviderRefreshDate(apiData);

  if (canRefreshNow) {
    return {
      action: 'refresh_now',
      primary_label: nextDateMmDdYy ? `Refresh Available ${nextDateMmDdYy}` : 'Refresh Report',
      subtext: null,
      skip_confirmation: true,
      requires_credentials: true,
      can_refresh_now: true,
      refresh_status: refreshStatus,
      refresh_available_date: nextDate,
    };
  }

  if (savedReportExists && !canRefreshNow) {
    if (hasCredentials) {
      return {
        action: 'refresh_now',
        primary_label: nextDateMmDdYy ? `Refresh Available ${nextDateMmDdYy}` : 'Refresh Report',
        subtext: null,
        skip_confirmation: true,
        requires_credentials: true,
        can_refresh_now: false,
        refresh_status: refreshStatus,
        refresh_available_date: nextDate,
      };
    }

    const purchaseAvailable =
      refreshStatus === REFRESH_STATUS.REFRESH_NOT_AVAILABLE_YET
      || apiData?.purchase_button_detected;
    return {
      action: 'connect',
      primary_label: 'Connect Provider',
      subtext: nextDateMmDdYy ? `Refresh Available ${nextDateMmDdYy}` : null,
      skip_confirmation: false,
      requires_credentials: true,
      can_refresh_now: false,
      refresh_status: refreshStatus,
      refresh_available_date: nextDate,
      secondary_action: purchaseAvailable && !savedReportExists ? 'purchase' : null,
      secondary_label: purchaseAvailable && !savedReportExists ? 'Purchase New Report' : null,
    };
  }

  if (
    refreshStatus === REFRESH_STATUS.REFRESH_NOT_AVAILABLE_YET
    || apiData?.purchase_button_detected
  ) {
    return {
      action: 'purchase',
      primary_label: 'Purchase Report',
      subtext: nextDateMmDdYy ? `Refresh Available ${nextDateMmDdYy}` : null,
      skip_confirmation: false,
      requires_credentials: false,
      can_refresh_now: false,
      refresh_status: refreshStatus,
      refresh_available_date: nextDate,
    };
  }

  if (hasCredentials) {
    return {
      action: 'refresh_now',
      primary_label: nextDateMmDdYy ? `Refresh Available ${nextDateMmDdYy}` : 'Refresh Report',
      subtext: null,
      skip_confirmation: true,
      requires_credentials: true,
      can_refresh_now: false,
      refresh_status: refreshStatus,
      refresh_available_date: nextDate,
    };
  }

  return {
    action: 'connect',
    primary_label: 'Connect Provider',
    subtext: null,
    skip_confirmation: false,
    requires_credentials: true,
    can_refresh_now: false,
    refresh_status: refreshStatus,
    refresh_available_date: nextDate,
  };
}

export function safeBuildRefreshActionState(apiData, options = {}) {
  try {
    return buildRefreshActionState(apiData, options);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[providerRefreshAction] using fallback action state', error);
    }
    return buildRefreshActionState(
      buildUnknownRefreshStatusPayload({
        providerKey: apiData?.provider_key || '',
        hasCredentials: options.hasCredentials,
      }),
      options,
    );
  }
}

export function getRefreshActionLabel(actionState, t) {
  const CR = 'creditReport';
  const action = actionState?.action;
  if (action === 'refresh_now' || action === 'check') {
    const formatted = formatRefreshDateMmDdYy(actionState?.refresh_available_date);
    if (formatted) return t(`${CR}.refreshAvailableOn`, { date: formatted });
    return t(`${CR}.refreshReport`);
  }
  if (action === 'view_report') return t(`${CR}.viewReport`, null, 'View Latest Report');
  if (action === 'purchase') return t(`${CR}.purchaseReport`);
  if (action === 'connect') return t(`${CR}.connectProvider`);
  return actionState?.primary_label || t(`${CR}.refreshReport`);
}

export function getRefreshActionSubtext(actionState, { lastUpdated, t } = {}) {
  const CR = 'creditReport';
  if (actionState?.subtext) {
    if (
      (actionState.action === 'purchase' || actionState.action === 'view_report')
      && actionState.refresh_available_date
    ) {
      const formatted = formatRefreshDateMmDdYy(actionState.refresh_available_date);
      return formatted ? t(`${CR}.refreshAvailableOn`, { date: formatted }) : actionState.subtext;
    }
    return actionState.subtext;
  }

  if (actionState?.action === 'refresh_now' && lastUpdated) {
    const formatted = formatLastUpdatedMmDdYy(lastUpdated);
    return formatted ? t('creditReport.lastUpdatedOn', { date: formatted }) : null;
  }

  return null;
}

export { REFRESH_STATUS };
